import type { ReactNode } from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  DISTRIBUTOR_WORKSPACE_STORAGE_KEY,
  fetchMyDistributorPrograms,
} from '@/lib/my-distributor'
import type {
  DistributorWorkspaceStored,
  MyDistributorGlobal,
  MyDistributorLoginContext,
  MyDistributorProfile,
  MyDistributorProgram,
} from '@/types/my-distributor'

interface DistributorWorkspaceContextValue {
  profile: MyDistributorProfile | null
  programs: MyDistributorProgram[]
  global: MyDistributorGlobal
  loginContext: MyDistributorLoginContext | undefined
  isLoading: boolean
  error: string | null
  reload: () => Promise<void>
  institutionId: number | null
  campaignId: number | null
  selectedProgram: MyDistributorProgram | null
  setInstitutionId: (id: number | null) => void
  setCampaignId: (id: number | null) => void
  hasWorkspaceContext: boolean
}

const DistributorWorkspaceContext = createContext<DistributorWorkspaceContextValue | undefined>(
  undefined
)

function readStoredWorkspace(): DistributorWorkspaceStored {
  if (typeof window === 'undefined') {
    return { institutionId: null, campaignId: null }
  }

  try {
    const raw = window.localStorage.getItem(DISTRIBUTOR_WORKSPACE_STORAGE_KEY)
    if (!raw) {
      return { institutionId: null, campaignId: null }
    }
    const parsed = JSON.parse(raw) as Partial<DistributorWorkspaceStored>
    return {
      institutionId:
        typeof parsed.institutionId === 'number' ? parsed.institutionId : null,
      campaignId: typeof parsed.campaignId === 'number' ? parsed.campaignId : null,
    }
  } catch {
    return { institutionId: null, campaignId: null }
  }
}

function writeStoredWorkspace(value: DistributorWorkspaceStored): void {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(DISTRIBUTOR_WORKSPACE_STORAGE_KEY, JSON.stringify(value))
}

function resolveWorkspaceSelection(
  programs: MyDistributorProgram[],
  stored: DistributorWorkspaceStored
): { institutionId: number | null; campaignId: number | null } {
  if (programs.length === 0) {
    return { institutionId: null, campaignId: null }
  }

  let institutionId = stored.institutionId
  let program =
    institutionId != null ? programs.find((p) => p.institution.id === institutionId) : undefined

  if (!program) {
    program = programs[0]
    institutionId = program.institution.id
  }

  let campaignId = stored.campaignId
  if (
    campaignId != null &&
    !program.allowed_campaigns.some((campaign) => campaign.id === campaignId)
  ) {
    campaignId = null
  }

  return { institutionId, campaignId }
}

export function DistributorWorkspaceProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<MyDistributorProfile | null>(null)
  const [programs, setPrograms] = useState<MyDistributorProgram[]>([])
  const [global, setGlobal] = useState<MyDistributorGlobal>({
    total_sales: 0,
    total_commission: 0,
  })
  const [loginContext, setLoginContext] = useState<MyDistributorLoginContext | undefined>(
    undefined
  )
  const [institutionId, setInstitutionIdState] = useState<number | null>(null)
  const [campaignId, setCampaignIdState] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const persistWorkspace = useCallback((institution: number | null, campaign: number | null) => {
    writeStoredWorkspace({ institutionId: institution, campaignId: campaign })
  }, [])

  const reload = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const res = await fetchMyDistributorPrograms()
      setProfile(res.profile)
      setPrograms(res.programs)
      setGlobal(res.global)
      setLoginContext(res.login_context)

      const stored = readStoredWorkspace()
      const selection = resolveWorkspaceSelection(res.programs, stored)
      setInstitutionIdState(selection.institutionId)
      setCampaignIdState(selection.campaignId)
      persistWorkspace(selection.institutionId, selection.campaignId)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Erro ao carregar seus dados de distribuidor.'
      )
    } finally {
      setIsLoading(false)
    }
  }, [persistWorkspace])

  useEffect(() => {
    void reload()
  }, [reload])

  const setInstitutionId = useCallback(
    (id: number | null) => {
      setInstitutionIdState(id)
      if (id == null) {
        setCampaignIdState(null)
        persistWorkspace(null, null)
        return
      }

      const program = programs.find((p) => p.institution.id === id)
      const nextCampaign =
        campaignId != null &&
        program?.allowed_campaigns.some((campaign) => campaign.id === campaignId)
          ? campaignId
          : null
      setCampaignIdState(nextCampaign)
      persistWorkspace(id, nextCampaign)
    },
    [programs, campaignId, persistWorkspace]
  )

  const setCampaignId = useCallback(
    (id: number | null) => {
      setCampaignIdState(id)
      persistWorkspace(institutionId, id)
    },
    [institutionId, persistWorkspace]
  )

  const selectedProgram = useMemo(
    () =>
      institutionId != null
        ? programs.find((p) => p.institution.id === institutionId) ?? null
        : null,
    [programs, institutionId]
  )

  const value = useMemo<DistributorWorkspaceContextValue>(
    () => ({
      profile,
      programs,
      global,
      loginContext,
      isLoading,
      error,
      reload,
      institutionId,
      campaignId,
      selectedProgram,
      setInstitutionId,
      setCampaignId,
      hasWorkspaceContext: selectedProgram !== null,
    }),
    [
      profile,
      programs,
      global,
      loginContext,
      isLoading,
      error,
      reload,
      institutionId,
      campaignId,
      selectedProgram,
      setInstitutionId,
      setCampaignId,
    ]
  )

  return (
    <DistributorWorkspaceContext.Provider value={value}>
      {children}
    </DistributorWorkspaceContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- hook exportado junto ao provider
export function useDistributorWorkspace(): DistributorWorkspaceContextValue {
  const ctx = useContext(DistributorWorkspaceContext)
  if (!ctx) {
    throw new Error('useDistributorWorkspace must be used within DistributorWorkspaceProvider')
  }
  return ctx
}
